import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from "@mui/material";

const personKey = (p) => `${String(p?.personType).toUpperCase()}:${p?.personId}`;

// Pick which of a group's people go onto which journey. Defaults to everyone
// selected (so the old "assign the whole group" behaviour is one click away),
// but lets you put a subset on a bus — e.g. departers splitting across flights.
export default function AssignToBusDialog({
  open,
  onClose,
  assignees = [],
  leaderKey,
  journeys = [],
  assigning = false,
  onAssign,
  label = "bus", // "bus" | "ticket block" — relabels the dialog for ticket blocks
}) {
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [journeyId, setJourneyId] = useState("");

  useEffect(() => {
    if (open) {
      setSelectedKeys(new Set(assignees.map(personKey)));
      setJourneyId("");
    }
    // Only re-seed when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (key) =>
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const selectAll = () => setSelectedKeys(new Set(assignees.map(personKey)));
  const clearAll = () => setSelectedKeys(new Set());

  const selectedAssignees = useMemo(
    () => assignees.filter((p) => selectedKeys.has(personKey(p))),
    [assignees, selectedKeys]
  );

  const selectedJourney = useMemo(
    () => journeys.find((j) => Number(j.id) === Number(journeyId)) || null,
    [journeys, journeyId]
  );

  const freeSeats = (journey) => {
    const cap = Number(journey?.capacity) || 0;
    const taken = Array.isArray(journey?.passengers) ? journey.passengers.length : 0;
    return cap ? Math.max(0, cap - taken) : null;
  };

  const handleAssign = () => {
    if (!journeyId || selectedAssignees.length === 0 || assigning) return;
    onAssign?.({ busId: Number(journeyId), members: selectedAssignees });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Add to {label}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="caption" sx={{ opacity: 0.7, flexGrow: 1 }}>
              {selectedAssignees.length} of {assignees.length} selected
            </Typography>
            <Button size="small" onClick={selectAll}>
              All
            </Button>
            <Button size="small" onClick={clearAll} disabled={selectedKeys.size === 0}>
              None
            </Button>
          </Stack>

          <List dense disablePadding sx={{ maxHeight: 240, overflow: "auto" }}>
            {assignees.map((p) => {
              const key = personKey(p);
              const isLeader = leaderKey && key === leaderKey;
              return (
                <ListItem key={key} disableGutters disablePadding>
                  <ListItemButton onClick={() => toggle(key)} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox
                        edge="start"
                        size="small"
                        checked={selectedKeys.has(key)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box component="span" fontWeight={isLeader ? 800 : 400}>
                          {(p.name || "").trim() || `#${p.personId}`}
                          {isLeader ? " · leader" : ""}
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>

          <FormControl size="small" fullWidth>
            <InputLabel id="assign-bus-journey">
              {label === "bus" ? "Bus" : "Ticket block"}
            </InputLabel>
            <Select
              labelId="assign-bus-journey"
              label={label === "bus" ? "Bus" : "Ticket block"}
              value={journeyId}
              onChange={(e) => setJourneyId(e.target.value)}
            >
              {journeys.length === 0 ? (
                <MenuItem value="" disabled>
                  No {label}s for this campus
                </MenuItem>
              ) : (
                journeys.map((j) => {
                  const free = freeSeats(j);
                  return (
                    <MenuItem key={j.id} value={String(j.id)} disabled={free === 0}>
                      {j.groupName || `Journey #${j.id}`}
                      {free !== null ? ` • ${free} free` : ""}
                    </MenuItem>
                  );
                })
              )}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={assigning}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={!journeyId || selectedAssignees.length === 0 || assigning}
        >
          {assigning
            ? "Assigning…"
            : `Assign ${selectedAssignees.length}${
                selectedJourney ? ` to ${selectedJourney.groupName || label}` : ""
              }`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
